import { Grid } from '@progress/kendo-react-grid';
import { RefObject, useEffect, useMemo, useState } from 'react';
import GoToPoDetails from 'src/components/GoToPoDetails';
import { GridColumn } from 'src/components/GridWrapper';
import AttachmentIconCell from 'src/components/custom-cells/AttachmentIconCell';
import AttachmentIconHeader from 'src/components/custom-headers/AttachmentIconHeader';
import {
  BlindReceivePermissions,
  CancelPoPermissions,
  CompleteReceiptPermissions,
  DeleteReceiptPermissions,
} from 'src/constants/permissionsList';
import usePermission from 'src/hooks/usePermissions';
import CreateNewReceipt from 'src/sections/view_receipts/cells/ReceiptIdCell';

export const useViewReceiptColumns: (
  tableRef: RefObject<Grid>,
  rows: any[]
) => GridColumn<any>[] = (tableRef, rows) => {
  const isBlindReceive: boolean = usePermission({ permissions: BlindReceivePermissions });
  const [gridSize, setGridSize] = useState(0);

  const columnFormat1Size = useMemo(() => {
    if (isBlindReceive) {
      return gridSize >= 1320 ? gridSize * 0.093 : 125;
    } else {
      return gridSize >= 1640 ? (gridSize + 40) * 0.074 : 125;
    }
  }, [isBlindReceive, gridSize]);

  const columnFormat2Size = useMemo(() => {
    if (isBlindReceive) {
      return gridSize >= 1320 ? gridSize * 0.12 : 150;
    } else {
      return gridSize >= 1640 ? (gridSize + 40) * 0.087 : 150;
    }
  }, [isBlindReceive, gridSize]);

  const showCompleteBtn = usePermission({
    permissions: CompleteReceiptPermissions,
    or: true,
  });
  const showDeleteBtn = usePermission({
    permissions: DeleteReceiptPermissions,
    or: true,
  });
  const showReopenBtn = usePermission({
    permissions: CompleteReceiptPermissions,
    or: true,
  });
  const showUnDeleteBtn = usePermission({
    permissions: DeleteReceiptPermissions,
    or: true,
  });

  const showCancelPOBtn = usePermission({
    permissions: CancelPoPermissions,
    or: true,
  });

  const showSelectedField = useMemo(
    () => showCompleteBtn || showDeleteBtn || showReopenBtn || showUnDeleteBtn || showCancelPOBtn,
    [showCancelPOBtn, showCompleteBtn, showDeleteBtn, showReopenBtn, showUnDeleteBtn]
  );

  useEffect(() => {
    const handleResize = () => {
      if (tableRef?.current?.element) setGridSize(tableRef.current.element.offsetWidth);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [tableRef]);

  const columns: GridColumn<any>[] = useMemo(
    () => [
      ...(showSelectedField && rows.length > 0
        ? ([
            {
              field: 'selected',
              title: '',
              filter: 'text',
              width: 40,
            },
          ] as const)
        : []),
      {
        field: 'numOfAttachments',
        title: '',
        component: AttachmentIconCell,
        headerCell: AttachmentIconHeader,
        width: 70,
      },
      {
        field: 'poNum',
        title: 'PO #',
        filter: 'text',
        component: GoToPoDetails,
        width: columnFormat1Size - 18,
        dataType: 'string',
      },
      {
        field: 'receiptId',
        title: 'Receipt #',
        filter: 'numeric',
        dataType: 'number',
        component: CreateNewReceipt,
        width: columnFormat1Size,
        filterFormat: '#',
        step: 1,
      },
      {
        field: 'receiptStatus',
        title: 'Receipt Status',
        filter: 'text',
        width: columnFormat2Size,
        dataType: 'string',
      },
      {
        field: 'buyerName',
        title: 'Buyer Name',
        filter: 'text',
        width: columnFormat2Size,
        dataType: 'string',
      },
      {
        field: 'receiptStatusDate',
        title: 'Receipt Status Date',
        filter: 'date',
        width: columnFormat1Size,
        dataType: 'date',
      },
      {
        field: 'supplierName',
        title: 'Supplier Name',
        filter: 'text',
        width: columnFormat2Size,
        dataType: 'string',
      },
      ...(isBlindReceive
        ? []
        : ([
            {
              field: 'poQty',
              title: 'PO TTL Qty',
              filter: 'numeric',
              filterFormat: 'n',
              width: columnFormat1Size - 28,
              dataType: 'number',
              step: 1,
            },
            {
              field: 'receiptTTLQty',
              title: 'Receipt TTL Qty',
              filter: 'numeric',
              width: columnFormat1Size,
              dataType: 'number',
              filterFormat: 'n',
              step: 1,
            },
          ] as const)),
      {
        field: 'currentReceiptQty',
        title: 'Current Receipt Qty',
        filter: 'numeric',
        width: columnFormat1Size,
        dataType: 'number',
        filterFormat: 'n',
        step: 1,
      },
      {
        field: 'poStatus',
        title: 'PO Status',
        filter: 'text',
        width: columnFormat1Size - 30,
        dataType: 'string',
      },
      {
        field: 'poDate',
        title: 'PO Date',
        filter: 'date',
        width: columnFormat1Size - 28,
        dataType: 'date',
      },
      ...(isBlindReceive
        ? []
        : ([
            {
              field: 'percentReceived',
              title: '% Received',
              filter: 'numeric',
              width: columnFormat1Size - 20,
              dataType: 'string',
              format: '{0:n2}%',
              filterFormat: 'p2',
              step: 0.01,
            },
          ] as const)),
      {
        field: 'receiptDate',
        title: 'Receipt Date',
        filter: 'date',
        width: columnFormat1Size,
        dataType: 'date',
      },
    ],
    [columnFormat1Size, columnFormat2Size, isBlindReceive, rows.length, showSelectedField]
  );
  return columns;
};
